import KYCFlowMaxErrors from '@/src/common/components/elements/Alerts/KYCFlowMaxErrors'
import PurchaseFlowMaxAlert from '@/src/common/components/elements/Alerts/PurchaseFlowMaxAlert'
import Button from '@/src/common/components/marketing/buttons/Button'
import { useCartContext } from '@/src/common/contexts/CartContext'
import { usePerfectGiftBoxContext } from '@/src/common/contexts/PerfectGiftBoxContext'
import { FormErrorDisplay } from '@/src/modules/build-a-card/FormErrorDisplay'
import {
    BuildACardStep,
    SimpleFieldWrapper,
} from '@/src/modules/build-a-card/StepElement'
import {
    CardImageButtonWrapper,
    HeaderParagraphText,
} from '@/src/modules/build-a-card/StyledComponents'
import ShippingInfoText from '@/src/modules/build-a-card/content/ShippingInfoText'
import EmbossFields from '@/src/modules/build-a-card/inputs/EmbossFields'
import { CustomImageModal } from '@/src/modules/build-a-card/modals/CustomImageModal'
import { PredesignsModal } from '@/src/modules/build-a-card/modals/PredesignsModal'
import PerfectGiftBoxDenomOptions from '@/src/modules/gift-box/buttons/PerfectGiftBoxDenomOptions'
import PerfectGiftBoxQuantitySelectorButtons from '@/src/modules/gift-box/buttons/PerfectGiftBoxQuantitySelectorButtons'
import { StyledForm } from '@/src/modules/pg-plus/PGPlusStyledComponents'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { MobileOnly } from '@/src/common/utilities/viewportDisplay'
import CtaButtons from '@/src/modules/gift-box/buttons/CtaButtons'
import OptionSeparator from '@/src/common/components/elements/Layout/OptionSeparator'
import {
    CUSTOM_IMAGE_TYPE,
    PREDESIGN_IMAGE_TYPE,
} from '@/src/modules/build-a-card/constants'
import KYCMaxPurchaseButtons from '@/src/modules/kyc/buttons/KYCMaxPurchaseButtons'
import { FieldWrapper } from '@/src/common/components/elements/Forms/StyledComponents'
import GiftBoxAddToCartButtons from '@/src/modules/build-a-card/content/GiftBoxAddToCartButtons'

const BoxStep2RightContent = () => {
    const {
        currentStep,
        isEditing,
        setDesignImage,
        setDesignName,
        setCustomDesignObject,
        predesignUuid,
        setPredesignUuid,
        predesigns,
        isLoading,
        setIsLoading,
        continueIsLoading,
        setContinueIsLoading,
        setDisplayLiveErrors,
        formErrors,
        validateAndAddCart,
        nameOnCard,
        setNameOnCard,
        messageOnCard,
        setMessageOnCard,
        KYCMinMaxLimits,
        selectedImageType,
        setSelectedImageType,
        designName,
    } = usePerfectGiftBoxContext()
    const {
        loadAmountExceedsKYCMax,
        numberOfCardsExceedsKYCMax,
        loadAmountMeetsKYCMin,
        numberOfCardsMeetsKYCMin,
    } = KYCMinMaxLimits
    const { cartTotalCards, setCartFlyoutOpen } = useCartContext()

    const router = useRouter()
    const [customImageModalIsOpen, setCustomImageModalIsOpen] =
        useState<boolean>(false)
    const [predesignsModalIsOpen, setPredesignsModalIsOpen] =
        useState<boolean>(false)
    const showKYCMaxError: boolean =
        (loadAmountExceedsKYCMax || numberOfCardsExceedsKYCMax) &&
        !continueIsLoading &&
        !isLoading
    const showKycLimitAlert =
        (loadAmountMeetsKYCMin || numberOfCardsMeetsKYCMin) && !showKYCMaxError
    const hasCustomImage = designName.toLowerCase() == CUSTOM_IMAGE_TYPE

    return (
        <BuildACardStep active={currentStep === 2}>
            <StyledForm id={'visa-box-form'} data-testid={'visa-box-form'}>
                <PerfectGiftBoxQuantitySelectorButtons />
                <PerfectGiftBoxDenomOptions />
                <FieldWrapper>
                    <HeaderParagraphText>
                        Now, choose a card design:
                    </HeaderParagraphText>
                    <CardImageButtonWrapper>
                        <Button
                            id={'upload-button'}
                            label={'Upload a photo'}
                            onClick={() => setCustomImageModalIsOpen(true)}
                            variant={
                                selectedImageType == CUSTOM_IMAGE_TYPE ||
                                (hasCustomImage && isEditing)
                                    ? 'graySquareButtonActive'
                                    : 'graySquareButton'
                            }
                            iconType={'image'}
                            type={'button'}
                        />
                        <CustomImageModal
                            isOpen={customImageModalIsOpen}
                            setIsOpen={setCustomImageModalIsOpen}
                            setDesignImage={setDesignImage}
                            setDesignName={setDesignName}
                            setCustomDesignObject={setCustomDesignObject}
                            setPredesignUuid={setPredesignUuid}
                            setSelectedImageType={setSelectedImageType}
                        />
                        <OptionSeparator padding={'0px 7%'} />
                        <Button
                            id={'choose-design-button'}
                            label={'Choose a design'}
                            onClick={() => setPredesignsModalIsOpen(true)}
                            variant={
                                selectedImageType == PREDESIGN_IMAGE_TYPE ||
                                (!hasCustomImage && isEditing)
                                    ? 'graySquareButtonActive'
                                    : 'graySquareButton'
                            }
                            iconType={'upload'}
                            type={'button'}
                        />
                    </CardImageButtonWrapper>
                    <PredesignsModal
                        isOpen={predesignsModalIsOpen}
                        setIsOpen={setPredesignsModalIsOpen}
                        setDesignImage={setDesignImage}
                        setDesignName={setDesignName}
                        setCustomDesignObject={setCustomDesignObject}
                        predesignUuid={predesignUuid}
                        setPredesignUuid={setPredesignUuid}
                        predesigns={predesigns}
                        headlineLabel={'wrapper designs'}
                        setSelectedImageType={setSelectedImageType}
                    />
                    <FormErrorDisplay
                        formErrors={formErrors}
                        path={'designImage'}
                    />
                </FieldWrapper>
                <EmbossFields
                    nameOnCard={nameOnCard}
                    setNameOnCard={setNameOnCard}
                    messageOnCard={messageOnCard}
                    setMessageOnCard={setMessageOnCard}
                    formErrors={formErrors}
                    headerText={'Finally, customize text on card:'}
                />
                {showKYCMaxError && <KYCFlowMaxErrors />}
                {showKycLimitAlert && <PurchaseFlowMaxAlert />}
                <SimpleFieldWrapper style={{ marginTop: 5 }}>
                    {showKYCMaxError ? (
                        <KYCMaxPurchaseButtons
                            isLoading={isLoading}
                            setIsLoading={setIsLoading}
                            cartTotalCards={cartTotalCards}
                            setCartFlyoutOpen={setCartFlyoutOpen}
                            router={router}
                        />
                    ) : (
                        <GiftBoxAddToCartButtons
                            isEditing={isEditing}
                            isLoading={isLoading}
                            continueIsLoading={continueIsLoading}
                            setDisplayLiveErrors={setDisplayLiveErrors}
                            setIsLoading={setIsLoading}
                            setContinueIsLoading={setContinueIsLoading}
                            validateAndAddCart={validateAndAddCart}
                        />
                    )}
                    <ShippingInfoText />
                </SimpleFieldWrapper>
            </StyledForm>
            <MobileOnly>
                <CtaButtons />
            </MobileOnly>
        </BuildACardStep>
    )
}

export default BoxStep2RightContent
